
import { Loader } from '@googlemaps/js-api-loader'
import { mapState } from 'vuex'
import { defaultParams } from '@/utils/constants'

import HeroButton from '@/components/HeroButton'
import InputField from '@/components/InputField'
import LocatorMap from '@/components/storeLocator/LocatorMap'
import Message from '@/components/Message.vue'
import StoresList from '@/components/storeLocator/StoresList'
import SearchIcon from '@/static/svg/nav/search-icon.svg'

export default {
  components: {
    HeroButton,
    InputField,
    LocatorMap,
    Message,
    StoresList,
    SearchIcon,
  },
  data() {
    return {
      searchInput: '',
      geocoder: null,
    }
  },
  computed: {
    ...mapState(['partnerId']),
    ...mapState('storeLocator', [
      'error',
      'storeLocatorConfig',
      'coordinates',
      'allStores',
      'nearestStores',
      'geocodingLibrary',
      'geometryLibrary',
      'coreLibrary',
      'resultsOffsetTop',
    ]),
    hasError() {
      return !!Object.keys(this.error).length
    },
    errorTitle() {
      if (this.error.type === 'browserLocation') {
        return this.$t('storeLocator.map.errors.browserLocation.title')
      }
      return this.$t('storeLocator.map.errors.generic.title')
    },
    errorDescription() {
      if (this.error.type === 'browserLocation') {
        return this.$t('storeLocator.map.errors.browserLocation.description')
      }
      return this.$t('storeLocator.map.errors.generic.description')
    },
    showIntroMessage() {
      return !this.nearestStores.length
    },
  },
  watch: {
    resultsOffsetTop() {
      const containerOffsetTop = this.$refs.resultsContainer.offsetTop
      this.$refs.resultsContainer.scrollTo({ top: this.resultsOffsetTop - containerOffsetTop })
    },
  },
  async mounted() {
    await this.$store.dispatch('storeLocator/getStoreLocatorConfig')
    if (this.hasError) {
      return
    }
    await this.loadGoogleMapsLibraries()
    if (this.hasError) {
      return
    }
    await this.setDefaultCoordinates()
  },
  methods: {
    async loadGoogleMapsLibraries() {
      try {
        const loader = new Loader({
          apiKey: this.storeLocatorConfig.maps_api_key || '',
          version: 'weekly',
        })
        const coreLibrary = await loader.importLibrary('core')
        this.$store.commit('storeLocator/SET_CORE_LIBRARY', coreLibrary)
        const mapsLibrary = await loader.importLibrary('maps')
        this.$store.commit('storeLocator/SET_MAPS_LIBRARY', mapsLibrary)
        const geocodingLibrary = await loader.importLibrary('geocoding')
        this.$store.commit('storeLocator/SET_GEOCODING_LIBRARY', geocodingLibrary)
        const markerLibrary = await loader.importLibrary('marker')
        this.$store.commit('storeLocator/SET_MARKER_LIBRARY', markerLibrary)
        const geometryLibrary = await loader.importLibrary('geometry')
        this.$store.commit('storeLocator/SET_GEOMETRY_LIBRARY', geometryLibrary)
      } catch (error) {
        this.handleError('libraries', error)
      }
    },
    handleError(type, error) {
      this.$store.commit('storeLocator/ADD_ERROR', { type, error })
    },
    clearError() {
      this.$store.commit('storeLocator/CLEAR_ERROR')
    },

    async geocode(location) {
      const geocoder = this.getGeocoder()
      try {
        const response = await geocoder.geocode({
          address: location || '',
          region: this.storeLocatorConfig.default_region || '',
          componentRestrictions: { country: this.storeLocatorConfig.default_region || '' },
        })
        return response
      } catch (error) {
        this.handleError('geocoding', error)
      }
    },
    getGeocoder() {
      if (!this.geocoder) {
        this.geocoder = new this.geocodingLibrary.Geocoder()
      }
      return this.geocoder
    },
    async retrieveAndSetStores() {
      if (!this.allStores.length) {
        const stores = await this.getAllStores()
        stores.forEach((store) => {
          const { latitude: lat, longitude: lng } = store.coordinates
          store.latLng = new this.coreLibrary.LatLng({ lat, lng })
        })
        this.$store.commit('storeLocator/SET_ALL_STORES', stores)
      }
    },
    // Recursively retrieve stores from endpoint
    async getAllStores(key = null, results = []) {
      const params = {
        'filters[partnerId]': this.partnerId,
        limit: 600,
        ...defaultParams,
        ...(key && { lastKey: key }),
      }
      let response
      try {
        response = await this.$axios.get('/external/stores', { params })
      } catch (error) {
        this.handleError('storesApi', error)
        return []
      }

      const { data, meta } = response.data
      const { lastKey } = meta
      results = [...results, ...data]

      // Base case - end of recursion as there are no more results to collect
      if (!lastKey || !results.length) {
        return results
      }

      return this.getAllStores(lastKey, results)
    },
    async setDefaultCoordinates() {
      const geocodeResult = await this.geocode(this.storeLocatorConfig.default_location)
      if (this.hasError) {
        return
      }
      const lat = geocodeResult.results[0]?.geometry?.location?.lat()
      const lng = geocodeResult.results[0]?.geometry?.location?.lng()
      const bounds = geocodeResult.results[0]?.geometry?.bounds
      this.$store.commit('storeLocator/SET_DEFAULT_COORDINATES', { lat, lng })
      this.$store.commit('storeLocator/SET_BOUNDS', bounds)
    },
    async setCoordinatesFromSearchInput() {
      this.clearError()
      const searchString = this.searchInput.trim()
      if (!searchString) {
        return
      }
      const geocodeResult = await this.geocode(searchString)
      if (this.hasError) {
        return
      }
      const lat = geocodeResult.results[0]?.geometry?.location?.lat()
      const lng = geocodeResult.results[0]?.geometry?.location?.lng()
      await this.updateLocationFromUserInput({ lat, lng })
    },
    async setCoordinatesFromBrowserLocation() {
      this.clearError()
      try {
        const { coords } = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject),
        )
        const { latitude: lat, longitude: lng } = coords
        await this.updateLocationFromUserInput({ lat, lng })
      } catch (error) {
        this.handleError('browserLocation', error)
      }
    },
    async updateLocationFromUserInput(coordinates) {
      this.$store.commit('storeLocator/SET_COORDINATES', coordinates)
      await this.retrieveAndSetStores()
      this.setNearestStores()
    },
    setNearestStores() {
      const distanceLimit = 50000
      const resultsLimit = 10
      const coordinatesLatLng = new this.coreLibrary.LatLng(this.coordinates)
      const nearestStores = this.allStores
        .map((store) => {
          const newStore = { ...store }
          newStore.distance = this.geometryLibrary.spherical.computeDistanceBetween(coordinatesLatLng, newStore.latLng)
          return newStore
        })
        .filter((store) => {
          return store.distance < distanceLimit
        })
        .sort((a, b) => a.distance - b.distance)
        .slice(0, resultsLimit)
      this.$store.commit('storeLocator/SET_NEAREST_STORES', nearestStores)
      this.$store.commit('storeLocator/SET_SELECTED_STORE', nearestStores[0] || {})
    },
  },
}
